import { Cartesian2, KeyboardEventModifier, ScreenSpaceEventType } from "cesium";
import { connectable, merge, of as observableOf } from "rxjs";
import { delay, filter, mergeMap, takeUntil, tap } from "rxjs/operators";
import { DtmCesiumLongPressEvents } from "./cesium-events.model";
import { DtmCesiumPureEventObserver } from "./cesium-pure-event-observer";
import { ThrottledCesiumEventBuilder } from "./throttled-cesium-event-builder";

const LONG_PRESS_EVENTS_MIN_DISTANCE_PX = 10;
const LONG_PRESS_EVENTS_DURATION = 250;

export class DtmCesiumLongPressObserver extends DtmCesiumPureEventObserver {
    public static LONG_PRESS_EVENTS_DURATION = LONG_PRESS_EVENTS_DURATION;
    public static LONG_PRESS_EVENTS_MIN_DISTANCE_PX = LONG_PRESS_EVENTS_MIN_DISTANCE_PX;

    constructor(
        protected event: DtmCesiumLongPressEvents,
        protected modifier: KeyboardEventModifier | undefined,
        private eventFactory: ThrottledCesiumEventBuilder
    ) {
        super(event, modifier);
    }

    public init() {
        let startEvent: ScreenSpaceEventType | undefined;
        let stopEvent: ScreenSpaceEventType | undefined;

        if (this.event === DtmCesiumLongPressEvents.LONG_LEFT_PRESS) {
            startEvent = ScreenSpaceEventType.LEFT_DOWN;
            stopEvent = ScreenSpaceEventType.LEFT_UP;
        } else if (this.event === DtmCesiumLongPressEvents.LONG_RIGHT_PRESS) {
            startEvent = ScreenSpaceEventType.RIGHT_DOWN;
            stopEvent = ScreenSpaceEventType.RIGHT_UP;
        } else if (this.event === DtmCesiumLongPressEvents.LONG_MIDDLE_PRESS) {
            startEvent = ScreenSpaceEventType.MIDDLE_DOWN;
            stopEvent = ScreenSpaceEventType.MIDDLE_UP;
        }

        if (!startEvent || !stopEvent) {
            throw new Error(`Unsupported long press event: ${this.event}`);
        }

        // Save start event position
        let startEventPosition: Cartesian2 | null = null;
        const startEventObservable = this.eventFactory
            .get(startEvent, this.modifier)
            ?.pipe(tap((movement) => (startEventPosition = movement.endPosition)));

        // Prevent drag mistaken for long press by observing mouse move far from start event position
        const mouseMoveEventObservable = this.eventFactory.get(ScreenSpaceEventType.MOUSE_MOVE)?.pipe(
            filter((movement) => {
                if (!startEventPosition) {
                    return false;
                }

                return (
                    Math.abs(movement.endPosition.x - startEventPosition.x) >
                        DtmCesiumLongPressObserver.LONG_PRESS_EVENTS_MIN_DISTANCE_PX ||
                    Math.abs(movement.endPosition.y - startEventPosition.y) > DtmCesiumLongPressObserver.LONG_PRESS_EVENTS_MIN_DISTANCE_PX
                );
            })
        );

        const stopEventObservable = this.eventFactory.get(stopEvent, this.modifier);

        if (!startEventObservable || !stopEventObservable || !mouseMoveEventObservable) {
            throw new Error(`Unsupported long press event: ${this.event}`);
        }

        // publish for preventing side effect
        return connectable(
            startEventObservable.pipe(
                mergeMap((e) =>
                    observableOf(e).pipe(
                        delay(DtmCesiumLongPressObserver.LONG_PRESS_EVENTS_DURATION),
                        takeUntil(merge(stopEventObservable, mouseMoveEventObservable))
                    )
                )
            )
        );
    }
}
